/* eslint-disable max-params */
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import {
  describe,
  PropTypes,
  useMemoMergePropsWithDefault,
  useValidateTypescriptPropTypes,
} from '@elliemae/ds-props-helpers';
import {
  addSpecialCharacters,
  getPartialMaskedPos,
  setCursorPosition,
  useCallbackAfterRender,
} from '../utils/index.js';
import { usePhoneMaskDefaultProps, usePhoneMaskPropTypes, type UsePhoneMaskT } from '../react-desc-prop-types.js';
import { usePhoneMaskName } from '../DSMaskDefinitions.js';

const phoneSpecialChars: [string, number][] = [
  ['(', 0],
  [') ', 4],
  [' - ', 9],
];

const internationalPhoneSpecialChars: [string, number][] = [
  ['+1 (', 0],
  [') ', 7],
  [' - ', 12],
];

const conformValue = (
  rawValue: string,
  cursorPos: number,
  lastKeyCode: string,
  opts: Omit<UsePhoneMaskT.Props, 'valueSetter'>,
) => {
  const characters = String(rawValue).split('');
  // Work with digits only
  const { isInternational = false } = opts;
  let maskedValue = characters.filter((char, idx, array) => {
    if (isInternational && idx === 1 && char === '1' && array[0] === '+') {
      return false;
    }
    return char >= '0' && char <= '9';
  });
  // We can't have zeros at the start of the array
  while (maskedValue.length && maskedValue[0] === '0') maskedValue.splice(0, 1);

  // We only consider first 10 digits
  maskedValue = maskedValue.slice(0, 10);

  const maskedPos = getPartialMaskedPos(rawValue, maskedValue, cursorPos);

  return addSpecialCharacters(
    maskedValue,
    isInternational ? internationalPhoneSpecialChars : phoneSpecialChars,
    maskedPos,
    lastKeyCode,
  );
};

export const usePhoneMask: ((args: UsePhoneMaskT.Props) => UsePhoneMaskT.OutputT) & { displayName: string } = (
  props,
) => {
  const propsWithDefault = useMemoMergePropsWithDefault<UsePhoneMaskT.InternalProps>(props, usePhoneMaskDefaultProps);
  useValidateTypescriptPropTypes(propsWithDefault, usePhoneMaskPropTypes, usePhoneMaskName);
  const { valueSetter, onChange: userOnChange, onKeyDown: userOnKeyDown, ...opts } = propsWithDefault;

  const lastKeyCode = useRef('Unidentified');

  const scheduleAfterRender = useCallbackAfterRender(true);

  // Some times, the value of the mask is not gonna change (user types invalid char for example)
  // This makes the input to go from "correct" -> "incorrect" -> "correct"
  // But since the input is controlled, the input does not re-render
  // This makes the input go mumbo-jumbo and places the cursor in the end, because we kinda changed the value async
  // Solution for this is to trigger a re-render, we do this with a set state call in the on change event
  const [, setKey] = useState(0);

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      lastKeyCode.current = e.code;
      if (userOnKeyDown) userOnKeyDown(e);
    },
    [userOnKeyDown],
  );

  const onChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const [mask, cursorPos] = conformValue(e.target.value, e.target.selectionEnd ?? 0, lastKeyCode.current, opts);

      valueSetter((prevMask) => {
        // Here we need to update the key
        if (mask === prevMask) setKey((key) => key + 1);
        return mask;
      });

      if (userOnChange) userOnChange(e, mask);

      scheduleAfterRender(() => setCursorPosition(e.target, cursorPos));
    },
    [valueSetter, scheduleAfterRender, opts, userOnChange],
  );

  return {
    onKeyDown,
    onChange,
  };
};

export const getPhoneMaskedValue = (value: string, opts?: Omit<UsePhoneMaskT.Props, 'valueSetter'>) => {
  const [maskedValue] = conformValue(value, value.length - 1, '', opts || {});
  return maskedValue;
};

usePhoneMask.displayName = usePhoneMaskName;
export const UsePhoneMaskWithSchema = describe(usePhoneMask);
UsePhoneMaskWithSchema.propTypes = usePhoneMaskPropTypes;
UsePhoneMaskWithSchema.returnType = {
  onKeyDown: PropTypes.func.description('On key down event for input').isRequired,
  onChange: PropTypes.func.description('On change event for input').isRequired,
};
